import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FaWhatsapp } from "react-icons/fa";
import { Calendar as CalendarIcon, ArrowLeft, ShieldCheck, UserCheck, FileSignature, Languages, Phone, Wrench, ClipboardCheck, Headset } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { tr as trLocale, az as azLocale, pl as plLocale } from "date-fns/locale";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BRAND } from "@/lib/brand";
import { LANGS, useT } from "@/lib/i18n";
import { OWNER_ROUTES, OWNER_TEXT } from "@/lib/owners.i18n";
import { OWNER_FAQ } from "@/lib/owners.faq";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const BENEFIT_ICONS = [ShieldCheck, UserCheck, FileSignature, Languages];
const MGMT_ICONS = [Wrench, ClipboardCheck, Headset];
const SITE = "https://unityagency.pl";
const LOCALES = { tr: trLocale, az: azLocale, pl: plLocale };
const DATE_PH = { pl: "Wybierz datę", en: "Pick a date", tr: "Tarih seçin", az: "Tarix seçin" };
const today = () => new Date(new Date().setHours(0, 0, 0, 0));

const fieldCls = "h-12 bg-navy-900 border-gold/25 text-white placeholder:text-slate-500 focus-visible:ring-gold focus-visible:border-gold rounded-xl";
const waUrl = (text) => `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(text)}`;

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-xs tracking-wider uppercase text-slate-400 mb-2">
      {label} {required && <span className="text-gold">*</span>}
    </span>
    {children}
  </label>
);

function OwnersHeader({ T, lang }) {
  return (
    <header className="max-w-6xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-4">
      <Link to="/" data-testid="owners-back-link" className="inline-flex items-center gap-3 group">
        <img src={BRAND.logoUrl} alt="Unity Real Estate" className="h-11 w-11 rounded-full object-cover ring-1 ring-gold/40" />
        <span className="inline-flex items-center gap-2 text-sm text-slate-300 group-hover:text-gold transition-colors">
          <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">{T.back}</span>
        </span>
      </Link>
      <div className="flex items-center gap-3">
        <div data-testid="owners-lang-switcher" className="flex items-center gap-1 rounded-full border border-gold/30 px-1 py-1">
          {LANGS.map((l) => (
            <Link key={l.code} to={OWNER_ROUTES[l.code]} data-testid={`owners-lang-${l.code}`} aria-current={lang === l.code ? "page" : undefined}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wider transition-colors ${lang === l.code ? "bg-gold text-navy-900" : "text-slate-400 hover:text-gold"}`}>
              {l.label}
            </Link>
          ))}
        </div>
        <a href={`tel:${BRAND.phoneRaw}`} data-testid="owners-phone" className="hidden md:inline-flex items-center gap-2 text-sm text-gold border border-gold/40 rounded-full px-4 py-2 hover:bg-gold/10 transition-colors">
          <Phone className="h-4 w-4" /> {BRAND.phone}
        </a>
      </div>
    </header>
  );
}

function OwnersIntro({ T }) {
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-6">
      <div className="text-xs tracking-[0.3em] text-gold uppercase mb-5">{T.eyebrow}</div>
      <h1 data-testid="owners-title" className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
        {T.title1} <span className="italic text-gold">{T.title2}</span>
      </h1>
      <p className="mt-7 text-base md:text-lg text-slate-300/90 leading-relaxed max-w-xl">{T.intro}</p>
      <div className="mt-10 grid sm:grid-cols-2 gap-5">
        {T.benefits.map(([t, x], i) => {
          const Icon = BENEFIT_ICONS[i];
          return (
            <motion.div key={t} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.08 }} data-testid={`owners-benefit-${i}`} className="rounded-2xl border border-gold/15 bg-navy-800/50 p-5">
              <Icon className="h-5 w-5 text-gold mb-3" />
              <div className="text-white font-medium mb-1">{t}</div>
              <div className="text-sm text-slate-400 leading-relaxed">{x}</div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}

function buildMessage(f, m, lang) {
  const line = (label, v) => (v ? `• ${label}: ${v}\n` : "");
  return (
    `${m.hi}${f.name ? m.named.replace("{name}", f.name) : ""}${m.intro}\n\n` +
    line(m.district, f.district) + line(m.address, f.address) + line(m.type, f.rooms) +
    line(m.size, f.size && `${f.size} m²`) + line(m.rent, f.rent && `${f.rent} ${m.perMonth}`) +
    line(m.from, f.from && format(f.from, "PPP", { locale: LOCALES[lang] })) + line(m.photos, f.photos) + line(m.notes, f.notes) +
    `\n${m.outro}`
  );
}

const EMPTY = { name: "", district: "", address: "", rooms: "", size: "", rent: "", from: undefined, photos: "", notes: "" };

function RoomsSelect({ T, value, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid="owners-rooms-trigger" className="h-12 bg-navy-900 border-gold/25 text-white data-[placeholder]:text-slate-500 focus:ring-gold rounded-xl">
        <SelectValue placeholder={T.f.choose} />
      </SelectTrigger>
      <SelectContent className="bg-navy-900 border-gold/25 text-white">
        {T.roomsList.map((r, i) => (
          <SelectItem key={r} value={r} data-testid={`owners-rooms-${i}`} className="focus:bg-gold/15 focus:text-gold-light cursor-pointer">{r}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function DatePick({ lang, value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="owners-from-trigger" className="h-12 w-full px-3 rounded-xl bg-navy-900 border border-gold/25 text-left text-sm hover:border-gold/60 transition-colors flex items-center justify-between">
          <span className={value ? "text-white" : "text-slate-500"}>
            {value ? format(value, "PPP", { locale: LOCALES[lang] }) : DATE_PH[lang]}
          </span>
          <CalendarIcon className="h-4 w-4 text-gold/80" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0 bg-navy-900 border-gold/25 text-white">
        <Calendar locale={LOCALES[lang]} mode="single" selected={value} onSelect={(d) => { onChange(d); setOpen(false); }} disabled={(d) => d < today()} initialFocus className="rounded-lg" />
      </PopoverContent>
    </Popover>
  );
}

function OwnersForm({ T, lang }) {
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const setVal = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const L = T.f;

  const submit = (e) => {
    e.preventDefault();
    if (!f.district || !f.rooms || !f.rent) {
      toast.error(T.err);
      return;
    }
    toast.success(T.ok);
    window.open(waUrl(buildMessage(f, T.msg, lang)), "_blank", "noopener,noreferrer");
  };

  return (
    <motion.form onSubmit={submit} data-testid="owners-form" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="lg:col-span-6 bg-navy-800/70 backdrop-blur-xl border border-gold/25 rounded-3xl p-7 md:p-9 shadow-2xl">
      <div className="font-serif text-2xl text-white mb-1">{T.formTitle}</div>
      <p className="text-sm text-slate-400 mb-7">{T.formNote}</p>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label={L.name}><Input data-testid="owners-name" value={f.name} onChange={set("name")} placeholder={L.namePh} className={fieldCls} /></Field>
        <Field label={L.district} required><Input data-testid="owners-district" value={f.district} onChange={set("district")} placeholder={L.districtPh} className={fieldCls} /></Field>
        <Field label={L.rooms} required>
          <RoomsSelect T={T} value={f.rooms} onChange={setVal("rooms")} />
        </Field>
        <Field label={L.size}><Input data-testid="owners-size" type="number" min="1" value={f.size} onChange={set("size")} placeholder={L.sizePh} className={fieldCls} /></Field>
        <Field label={L.rent} required><Input data-testid="owners-rent" type="number" min="1" value={f.rent} onChange={set("rent")} placeholder={L.rentPh} className={fieldCls} /></Field>
        <Field label={L.from}><DatePick lang={lang} value={f.from} onChange={setVal("from")} /></Field>
        <div className="sm:col-span-2"><Field label={L.address}><Input data-testid="owners-address" value={f.address} onChange={set("address")} placeholder={L.addressPh} className={fieldCls} /></Field></div>
        <div className="sm:col-span-2"><Field label={L.photos}><Input data-testid="owners-photos" value={f.photos} onChange={set("photos")} placeholder={L.photosPh} className={fieldCls} /></Field></div>
        <div className="sm:col-span-2"><Field label={L.notes}><Textarea data-testid="owners-notes" value={f.notes} onChange={set("notes")} placeholder={L.notesPh} className="bg-navy-900 border-gold/25 text-white placeholder:text-slate-500 focus-visible:ring-gold rounded-xl min-h-[90px]" /></Field></div>
      </div>
      <button type="submit" data-testid="owners-submit" className="mt-7 w-full inline-flex items-center justify-center gap-3 bg-gold hover:bg-gold-light text-navy-900 font-semibold min-h-[56px] rounded-full transition-[background-color,transform] hover:-translate-y-0.5">
        <FaWhatsapp className="h-5 w-5" /> {T.submit}
      </button>
    </motion.form>
  );
}

function OwnersManagement({ T }) {
  const M = T.mgmt;
  return (
    <section data-testid="owners-management" className="max-w-6xl mx-auto px-6 md:px-10 pt-24">
      <div className="rounded-3xl border border-gold/25 bg-navy-800/50 p-7 md:p-12 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="text-xs tracking-[0.3em] text-gold uppercase mb-4">{M.eyebrow}</div>
          <h2 className="font-serif text-3xl lg:text-4xl leading-tight">{M.title1} <span className="italic text-gold">{M.title2}</span></h2>
          <p className="mt-5 text-slate-400 leading-relaxed">{M.text}</p>
          <div className="mt-6 text-sm text-slate-300">{M.priceLabel} <span data-testid="owners-management-price" className="text-gold font-medium">{M.price}</span></div>
          <a href={waUrl(M.msg)} target="_blank" rel="noopener noreferrer" data-testid="owners-management-cta" className="mt-7 inline-flex items-center gap-3 bg-gold hover:bg-gold-light text-navy-900 font-semibold px-7 min-h-[52px] rounded-full transition-[background-color,transform] hover:-translate-y-0.5">
            <FaWhatsapp className="h-5 w-5" /> {M.cta}
          </a>
        </div>
        <div className="lg:col-span-7 grid gap-4">
          {M.items.map(([t, x], i) => {
            const Icon = MGMT_ICONS[i];
            return (
              <div key={t} data-testid={`owners-management-item-${i}`} className="flex gap-4 rounded-2xl border border-gold/15 bg-navy-900/60 p-5">
                <Icon className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                <div>
                  <div className="text-white font-medium mb-1">{t}</div>
                  <div className="text-sm text-slate-400 leading-relaxed">{x}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function OwnersSteps({ T }) {
  return (
    <section className="max-w-6xl mx-auto px-6 md:px-10 py-20" data-testid="owners-steps">
      <div className="text-xs tracking-[0.3em] text-gold uppercase mb-4">{T.stepsEyebrow}</div>
      <h2 className="font-serif text-3xl lg:text-4xl mb-10">{T.stepsTitle1} <span className="italic text-gold">{T.stepsTitle2}</span></h2>
      <div className="grid md:grid-cols-4 gap-6">
        {T.steps.map(([t, x], i) => (
          <div key={t} className="border-t border-gold/30 pt-5">
            <div className="font-serif text-3xl text-gold/70 mb-3">0{i + 1}</div>
            <div className="text-white font-medium mb-2">{t}</div>
            <div className="text-sm text-slate-400 leading-relaxed">{x}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function OwnersFAQ({ lang }) {
  const F = OWNER_FAQ[lang];
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "ld-owner-faq";
    el.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: F.items.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
    });
    document.head.appendChild(el);
    return () => el.remove();
  }, [F]);
  return (
    <section data-testid="owners-faq" className="max-w-4xl mx-auto px-6 md:px-10 pb-20">
      <div className="text-xs tracking-[0.3em] text-gold uppercase mb-4">{F.eyebrow}</div>
      <h2 className="font-serif text-3xl lg:text-4xl mb-8">{F.title1} <span className="italic text-gold">{F.title2}</span></h2>
      <Accordion type="single" collapsible className="space-y-3">
        {F.items.map(([q, a], i) => (
          <AccordionItem key={q} value={`q${i}`} className="border border-gold/15 rounded-2xl bg-navy-800/50 px-5">
            <AccordionTrigger data-testid={`owners-faq-q${i}`} className="text-left text-white hover:text-gold hover:no-underline">{q}</AccordionTrigger>
            <AccordionContent data-testid={`owners-faq-a${i}`} className="text-slate-400 leading-relaxed">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

function useOwnerMeta(T, lang) {
  useEffect(() => {
    const setAttr = (sel, attr, val) => {
      const el = document.querySelector(sel);
      if (!el) return null;
      const prev = el.getAttribute(attr);
      el.setAttribute(attr, val);
      return () => el.setAttribute(attr, prev);
    };
    const prevTitle = document.title;
    const url = `${SITE}${OWNER_ROUTES[lang]}`;
    document.title = T.metaTitle;
    const restores = [
      setAttr('link[rel="canonical"]', "href", url),
      setAttr('meta[property="og:url"]', "content", url),
      setAttr('meta[name="description"]', "content", T.metaDesc),
      setAttr('meta[property="og:description"]', "content", T.metaDesc),
      setAttr('meta[property="og:title"]', "content", T.metaTitle),
    ];
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      restores.forEach((r) => r && r());
    };
  }, [T, lang]);
}

export default function Owners({ lang }) {
  const T = OWNER_TEXT[lang];
  const { lang: siteLang, setLang } = useT();
  useOwnerMeta(T, lang);
  useEffect(() => {
    if (siteLang !== lang) setLang(lang);
  }, [lang, siteLang, setLang]);

  return (
    <main lang={lang} data-testid="owners-page" className="relative min-h-screen bg-[#0B1733] text-white overflow-x-hidden">
      <OwnersHeader T={T} lang={lang} />
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-8 lg:pt-14 grid lg:grid-cols-12 gap-12 items-start">
        <OwnersIntro T={T} />
        <OwnersForm key={lang} T={T} lang={lang} />
      </section>
      <OwnersManagement T={T} />
      <OwnersSteps T={T} />
      <OwnersFAQ lang={lang} />
      <footer className="border-t border-gold/10 py-8 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} {BRAND.name} · {T.city}
      </footer>
    </main>
  );
}
